/**
 * <RoiCalculator /> — the live, screen-shareable ROI panel of the Discovery
 * Call Report (ROI-v2: conservative, brain-dead-simple, geo-local).
 *
 * Four AI-seeded, rep-editable inputs — runsPerYear, peopleInvolved, a
 * localized loadedHourlyCost (conservative regional rate, rep can override),
 * and moxoAnnualCost (a MANUAL FE-only input, never server-provided) — feed the
 * shared `computeRoi` mirror (frontend/src/lib/sales-enablement-roi ≡ backend)
 * so the math never drifts from the server. Recomputes on every keystroke.
 *
 * Reframe rationale (the pitch must feel REAL, not vendor fantasy):
 *   - The hero is a single plain-English sentence + four tiles led by HOURS
 *     FREED (with an FTE-equivalent) and a plain return MULTIPLE ("6.8×") — the
 *     four-digit % is demoted to a subtext, never the headline.
 *   - A scenario BAND (Conservative / Expected / Aggressive, default
 *     Conservative) scales the modest per-person-per-run hours; a "What this
 *     assumes" tooltip spells each band out in plain words.
 *   - The old "soft levers" become "what we'll measure during the pilot" — the
 *     four real outcome categories (cycle-time, throughput, SLA, cost/process),
 *     each labeled directional and validated for real in the pilot, never a
 *     hardcoded guarantee.
 *
 * Design: funnel editorial tokens only (no ad-hoc hex); works in light + dark;
 * big serif numerals legible at across-the-room screen-share zoom.
 */
import { useId, useMemo, useState } from 'react';
import {
  BAND_MULTIPLIERS,
  computeRoi,
  regionLabel,
  type RoiAssumptions,
  type RoiScenario,
} from '../../lib/sales-enablement-roi';

export interface RoiCalculatorDefaults {
  runsPerYear: number;
  runsRationale: string;
  peopleInvolved: number;
  peopleRationale: string;
  /** EXPECTED hours saved per person per run — the band scales this. */
  hoursSavedPerRun: number;
  hoursRationale: string;
  /** Conservative regional loaded hourly cost (editable on screen). */
  loadedHourlyCost: number;
  cycleDaysCurrent: number;
  cycleDaysTarget: number;
  errorRatePct: number;
  reworkHoursPerError: number;
  roiNarrative: string;
  /** Region key from PDL (drives the localized rate hint + band tooltip). */
  region?: string;
}

const BANDS: { key: RoiScenario; label: string }[] = [
  { key: 'conservative', label: 'Conservative' },
  { key: 'expected', label: 'Expected' },
  { key: 'aggressive', label: 'Aggressive' },
];

const BAND_LEAD_IN: Record<RoiScenario, string> = {
  conservative: 'Even conservatively,',
  expected: 'On our expected case,',
  aggressive: 'On the upside,',
};

const toNum = (s: string): number => {
  const v = Number(s);
  return Number.isFinite(v) ? v : 0;
};

const money = (x: number): string =>
  `${x < 0 ? '-' : ''}$${Math.abs(Math.round(x)).toLocaleString('en-US')}`;

const roundInt = (x: number): string => Math.round(x).toLocaleString('en-US');
const oneDp = (x: number): string => x.toFixed(1);

export default function RoiCalculator({
  roiDefaults,
  totalLeakMinutesPerRun,
  stepCount,
}: {
  roiDefaults: RoiCalculatorDefaults;
  /** Summed per-step leak (min per person per run) from the process section —
   *  GROUNDS the recovered-minutes claim. Optional: falls back gracefully. */
  totalLeakMinutesPerRun?: number;
  /** Number of process steps the leak is summed across. */
  stepCount?: number;
}) {
  const [runsPerYear, setRunsPerYear] = useState<string>(String(roiDefaults.runsPerYear));
  const [peopleInvolved, setPeopleInvolved] = useState<string>(String(roiDefaults.peopleInvolved));
  const [loadedHourlyCost, setLoadedHourlyCost] = useState<string>(String(roiDefaults.loadedHourlyCost));
  const [moxoAnnualCost, setMoxoAnnualCost] = useState<string>('');
  const [includeRework, setIncludeRework] = useState<boolean>(true);
  const [scenario, setScenario] = useState<RoiScenario>('conservative');

  const tooltipId = useId();
  const region = regionLabel(roiDefaults.region);

  const assumptions: RoiAssumptions = useMemo(
    () => ({
      hoursSavedPerRun: roiDefaults.hoursSavedPerRun,
      loadedHourlyCost: toNum(loadedHourlyCost),
      cycleDaysCurrent: roiDefaults.cycleDaysCurrent,
      cycleDaysTarget: roiDefaults.cycleDaysTarget,
      errorRatePct: roiDefaults.errorRatePct,
      reworkHoursPerError: roiDefaults.reworkHoursPerError,
    }),
    [roiDefaults, loadedHourlyCost],
  );

  const out = useMemo(
    () =>
      computeRoi(
        {
          runsPerYear: toNum(runsPerYear),
          peopleInvolved: toNum(peopleInvolved),
          moxoAnnualCost: toNum(moxoAnnualCost),
        },
        assumptions,
        { includeReworkInTotal: includeRework, scenario },
      ),
    [runsPerYear, peopleInvolved, moxoAnnualCost, assumptions, includeRework, scenario],
  );

  const rate = toNum(loadedHourlyCost);
  const cost = toNum(moxoAnnualCost);
  const expectedHours = roiDefaults.hoursSavedPerRun;
  const minPerRun = Math.round(out.effectiveHoursSavedPerRun * 60);

  // Hero tile display strings (null-safe — never NaN/Infinity/undefined). When
  // no Moxo price is entered yet, payback/return show a placeholder rather than
  // an "immediate / 0-month" figure that reads as a bug before a price is set.
  const hoursFreedText = roundInt(out.annualHoursFreed);
  const fteText = oneDp(out.fteEquivalent);
  const netText = money(out.netAnnualBenefit);
  const paybackTileText = cost <= 0 || out.paybackMonths == null ? '—' : `${out.paybackMonths.toFixed(1)} mo`;
  const multipleText = out.returnMultiple == null ? '—' : `${out.returnMultiple.toFixed(1)}×`;
  const roiPctSub = out.roiPct == null ? 'Add your Moxo price' : `≈ ${roundInt(out.roiPct)}% ROI`;

  // One plain-English headline sentence, built live from the numbers.
  let paybackClause: string;
  if (cost <= 0) {
    paybackClause = 'Add your Moxo price to see payback and return.';
  } else if (out.paybackMonths == null) {
    paybackClause = `Against ${money(cost)}, the modeled benefit doesn't cover it at these assumptions.`;
  } else {
    paybackClause = `Against ${money(cost)}, it pays for itself in ~${out.paybackMonths.toFixed(1)} months.`;
  }
  // GROUND the recovered-minutes claim in the summed per-step process leak when
  // it is available: Moxo wins back a believable FRACTION of what leaks. Fall
  // back gracefully to the ungrounded runs×people framing when the leak is
  // missing/zero (e.g. an older report body without leakMinutes).
  const hasLeak = typeof totalLeakMinutesPerRun === 'number' && totalLeakMinutesPerRun > 0;
  const stepsClause = stepCount && stepCount > 0 ? ` across ${roundInt(stepCount)} steps` : '';
  // Use the same dollar figure as the NET ANNUAL SAVINGS tile so the headline
  // and tile never disagree (labor + optional redo − Moxo cost).
  const worthText = money(out.netAnnualBenefit);
  const headlineSentence = hasLeak
    ? `Each run leaks about ${roundInt(totalLeakMinutesPerRun)} min per person${stepsClause}. ` +
      `${BAND_LEAD_IN[scenario]} Moxo wins back ~${roundInt(minPerRun)} min of that — ` +
      `~${hoursFreedText} hours a year (~${fteText} of a person), worth ~${worthText}. ` +
      paybackClause
    : `You run this ${roundInt(toNum(runsPerYear))}× a year with ${roundInt(toNum(peopleInvolved))} people. ` +
      `${BAND_LEAD_IN[scenario]} Moxo gives each person back ~${roundInt(minPerRun)} min per run — ` +
      `~${hoursFreedText} hours a year (~${fteText} of a person), worth ~${worthText}. ` +
      paybackClause;

  return (
    <section className="se-roi" aria-label="ROI calculator">
      <header className="se-roi__head">
        <h3 className="se-section-title">Return on investment</h3>
        <p className="se-roi__narrative">{roiDefaults.roiNarrative}</p>
      </header>

      {/* Scenario band + "what this assumes" tooltip ---------------------- */}
      <div className="se-roi__band-row">
        <div className="se-band" role="radiogroup" aria-label="Savings scenario">
          {BANDS.map((b) => (
            <button
              key={b.key}
              type="button"
              role="radio"
              aria-checked={scenario === b.key}
              className={'se-band__opt' + (scenario === b.key ? ' is-active' : '')}
              onClick={() => setScenario(b.key)}
            >
              {b.label}
            </button>
          ))}
        </div>

        <span className="se-tip">
          <button
            type="button"
            className="se-tip__trigger"
            aria-describedby={tooltipId}
          >
            What this assumes
          </button>
          <span role="tooltip" id={tooltipId} className="se-tip__bubble">
            Conservative: ~{oneDp(expectedHours * BAND_MULTIPLIERS.conservative)} hr saved per person per run
            at ${roundInt(rate)}/hr (localized to {region}). Expected: ~{oneDp(expectedHours * BAND_MULTIPLIERS.expected)} hr.
            Aggressive: ~{oneDp(expectedHours * BAND_MULTIPLIERS.aggressive)} hr. The other gains
            below are rough numbers we prove in your pilot.
          </span>
        </span>
      </div>

      {/* Editable inputs -------------------------------------------------- */}
      <div className="se-roi__inputs">
        <div className="se-field">
          <label className="se-field__label" htmlFor="se-roi-runs">Runs per year</label>
          <input
            id="se-roi-runs"
            type="number"
            className="se-field__input"
            value={runsPerYear}
            min={0}
            onChange={(e) => setRunsPerYear(e.target.value)}
          />
          <span className="se-field__hint">{roiDefaults.runsRationale}</span>
        </div>

        <div className="se-field">
          <label className="se-field__label" htmlFor="se-roi-people">People involved</label>
          <input
            id="se-roi-people"
            type="number"
            className="se-field__input"
            value={peopleInvolved}
            min={0}
            onChange={(e) => setPeopleInvolved(e.target.value)}
          />
          <span className="se-field__hint">
            Number of people in one run of this work — not the whole company.
          </span>
        </div>

        <div className="se-field">
          <label className="se-field__label" htmlFor="se-roi-rate">Cost per hour</label>
          <input
            id="se-roi-rate"
            type="number"
            className="se-field__input"
            value={loadedHourlyCost}
            min={0}
            onChange={(e) => setLoadedHourlyCost(e.target.value)}
          />
          <span className="se-field__hint">
            What one hour of this team&rsquo;s time costs — set for {region}. Change it if needed.
          </span>
        </div>

        <div className="se-field">
          <label className="se-field__label" htmlFor="se-roi-cost">Moxo annual cost</label>
          <input
            id="se-roi-cost"
            type="number"
            className="se-field__input"
            value={moxoAnnualCost}
            min={0}
            placeholder="0"
            onChange={(e) => setMoxoAnnualCost(e.target.value)}
          />
          <span className="se-field__hint">You type this — the price you plan to quote.</span>
        </div>
      </div>

      {/* One plain-English headline sentence ------------------------------ */}
      <p className="se-roi__headline" data-testid="roi-headline-sentence">{headlineSentence}</p>

      {/* Headline tiles: hours freed → net → payback → return multiple --- */}
      <div className="se-roi__tiles" data-testid="roi-headline">
        <div className="se-tile se-tile--accent">
          <div className="se-tile__label">Hours freed / year</div>
          <div className="se-tile__value" data-testid="roi-hours-freed">{hoursFreedText}</div>
          <div className="se-tile__sub" data-testid="roi-fte">≈ {fteText} of a person</div>
        </div>
        <div className="se-tile se-tile--accent">
          <div className="se-tile__label">Net annual savings</div>
          <div className="se-tile__value" data-testid="roi-net-benefit">{netText}</div>
          <div className="se-tile__sub" data-testid="roi-net-caption">
            {cost <= 0 ? 'No Moxo price entered yet' : 'After your Moxo cost'}
          </div>
        </div>
        <div className="se-tile">
          <div className="se-tile__label">Payback</div>
          <div className="se-tile__value" data-testid="roi-payback-months">{paybackTileText}</div>
          <div className="se-tile__sub">Months to break even</div>
        </div>
        <div className="se-tile">
          <div className="se-tile__label">Return</div>
          <div className="se-tile__value" data-testid="roi-return-multiple" title={roiPctSub}>{multipleText}</div>
          <div className="se-tile__sub" data-testid="roi-roi-pct">{roiPctSub}</div>
        </div>
      </div>

      {/* Pilot-validated outcomes (was: soft levers) --------------------- */}
      <div className="se-roi__pilot" data-testid="roi-pilot-outcomes">
        <div className="se-roi__pilot-head">
          <h4 className="se-roi__pilot-title">What we&rsquo;ll check on your real work during the pilot.</h4>
          <label className="se-toggle">
            <input
              type="checkbox"
              checked={includeRework}
              onChange={(e) => setIncludeRework(e.target.checked)}
            />
            <span>Also count redo work</span>
          </label>
        </div>
        <ul className="se-roi__pilot-list">
          <li>
            <span className="se-roi__pilot-metric">Faster turnaround</span>
            <span className="se-roi__pilot-desc">
              ~{out.cycleDaysSaved} days faster each time
              (~{roundInt(toNum(runsPerYear))} runs a year).
              <em className="se-roi__pilot-tag"> a rough number — we prove it in the pilot</em>
            </span>
          </li>
          <li>
            <span className="se-roi__pilot-metric">More work done per person</span>
            <span className="se-roi__pilot-desc">
              The same team finishes more each week.
              <em className="se-roi__pilot-tag"> a rough number — we prove it in the pilot</em>
            </span>
          </li>
          <li>
            <span className="se-roi__pilot-metric">Fewer missed deadlines</span>
            <span className="se-roi__pilot-desc">
              Fewer missed steps and due dates, with one clear record.
              <em className="se-roi__pilot-tag"> a rough number — we prove it in the pilot</em>
            </span>
          </li>
          <li>
            <span className="se-roi__pilot-metric">Lower cost per job</span>
            <span className="se-roi__pilot-desc">
              ~{money(out.reworkSavings)} / {roundInt(out.reworkHoursSaved)} hours of redo work removed per year
              {includeRework ? ' (counted in the total above)' : ' (not counted in the total above)'}.
              <em className="se-roi__pilot-tag"> a rough number — we prove it in the pilot</em>
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}
